import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import DeviceCard from './components/DeviceCard';
import AddDeviceModal from './components/AddDeviceModal';
import ConfigureDeviceModal from './components/ConfigureDeviceModal';
import SyncStatusPanel from './components/SyncStatusPanel';
import { wearableConnectionService } from '../../services/wearablesService';
import { useAuth } from '../../contexts/AuthContext';

const DeviceIntegrationHub = () => {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [syncStatus, setSyncStatus] = useState({});

  useEffect(() => {
    if (user) {
      loadDevices();
    }
  }, [user]);

  const loadDevices = async () => {
    setLoading(true);
    const { data, error } = await wearableConnectionService?.getConnections(user?.id);
    if (!error && data) {
      setDevices(data);
    }
    setLoading(false);
  };

  const handleAddDevice = async (deviceData) => {
    const { data, error } = await wearableConnectionService?.addConnection(deviceData);
    if (!error && data) {
      setDevices([...devices, data]);
      setIsAddModalOpen(false);
    }
    return { data, error };
  };

  const handleConfigureDevice = (device) => {
    setSelectedDevice(device);
    setIsConfigureModalOpen(true);
  };

  const handleUpdateDevice = async (deviceId, updates) => {
    const { data, error } = await wearableConnectionService?.updateConnection(deviceId, updates);
    if (!error && data) {
      setDevices(devices?.map(d => d?.id === deviceId ? data : d));
      setIsConfigureModalOpen(false);
    }
    return { data, error };
  };

  const handleDeleteDevice = async (deviceId) => {
    const { error } = await wearableConnectionService?.deleteConnection(deviceId);
    if (!error) {
      setDevices(devices?.filter(d => d?.id !== deviceId));
    }
  };

  const handleTestConnection = async (deviceId) => {
    setSyncStatus({ ...syncStatus, [deviceId]: 'testing' });
    const { success, error } = await wearableConnectionService?.testConnection(deviceId);
    setSyncStatus({ ...syncStatus, [deviceId]: success ? 'success' : 'error' });
    setTimeout(() => {
      setSyncStatus({ ...syncStatus, [deviceId]: null });
    }, 3000);
    return { success, error };
  };

  const supportedDevices = [
    { type: 'garmin', name: 'Garmin', icon: 'Watch', color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
    { type: 'apple_watch', name: 'Apple Watch', icon: 'Watch', color: 'text-gray-700', bgColor: 'bg-gray-700/10' },
    { type: 'whoop', name: 'Whoop', icon: 'Activity', color: 'text-red-500', bgColor: 'bg-red-500/10' },
    { type: 'hume', name: 'Hume Band', icon: 'Watch', color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
    { type: 'polar', name: 'Polar', icon: 'Heart', color: 'text-cyan-500', bgColor: 'bg-cyan-500/10' }
  ];

  return (
    <>
      <Helmet>
        <title>Device Integration Hub - FormForge</title>
        <meta name="description" content="Connect and manage your wearable devices for seamless health data synchronization" />
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-6 lg:py-8">
          <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)' }}>
                  <Icon name="Watch" size={24} style={{ color: '#00D4FF' }} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Device Integration Hub</h1>
                  <p className="text-sm" style={{ color: '#6B7280' }}>Connect and manage your wearable devices</p>
                </div>
              </div>
              <button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)', color: '#fff', boxShadow: '0 0 16px rgba(255,107,0,0.3)' }}>
                <Icon name="Plus" size={18} />
                Add Device
              </button>
            </div>
          </motion.div>

          {/* Supported Devices */}
          <div className="mb-8 rounded-xl p-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#E8EAED' }}>Supported Devices</h2>
            <div className="flex flex-wrap gap-3">
              {supportedDevices?.map((device) => (
                <div key={device?.type} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name={device?.icon} size={16} style={{ color: '#00D4FF' }} />
                  <span className="text-sm" style={{ color: '#E8EAED' }}>{device?.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sync Status Panel */}
          <SyncStatusPanel devices={devices} syncStatus={syncStatus} />

          {/* Connected Devices */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#E8EAED' }}>Connected Devices</h2>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#FF6B00' }}></div>
              </div>
            ) : devices?.length === 0 ? (
              <div className="text-center py-16 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Icon name="Watch" size={48} className="mx-auto mb-4" style={{ color: '#6B7280' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E8EAED' }}>No devices connected</h3>
                <p className="mb-4" style={{ color: '#6B7280' }}>Connect your first wearable device to start syncing health data</p>
                <button onClick={() => setIsAddModalOpen(true)} className="px-6 py-2 rounded-xl text-sm font-medium" style={{ background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' }}>Add Your First Device</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {devices?.map((device) => (
                  <DeviceCard key={device?.id} device={device} syncStatus={syncStatus?.[device?.id]} onConfigure={handleConfigureDevice} onDelete={handleDeleteDevice} onTestConnection={handleTestConnection} onTest={handleTestConnection} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
      {isAddModalOpen && <AddDeviceModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={handleAddDevice} supportedDevices={supportedDevices} />}
      {isConfigureModalOpen && selectedDevice && <ConfigureDeviceModal isOpen={isConfigureModalOpen} device={selectedDevice} onClose={() => setIsConfigureModalOpen(false)} onUpdate={handleUpdateDevice} />}
    </>
  );
};

export default DeviceIntegrationHub;
